/**
 * Mobile photo capture page — opened via QR code on the user's phone.
 * Route: /capture/:token
 *
 * Starts the rear camera, lets the user take a photo, then POSTs it to the
 * relay endpoint so the desktop session can receive it.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Text,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CameraRegular,
  ArrowCounterclockwiseRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#000',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    boxSizing: 'border-box',
  },
  video: {
    width: '100%',
    maxWidth: '480px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#000',
  },
  preview: {
    width: '100%',
    maxWidth: '480px',
    borderRadius: tokens.borderRadiusMedium,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  statusIcon: {
    fontSize: '64px',
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusText: {
    color: tokens.colorNeutralForegroundOnBrand,
    textAlign: 'center',
  },
});

type Stage = 'starting' | 'camera' | 'review' | 'uploading' | 'done' | 'error';

const CaptureApp: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const styles = useStyles();

  const [stage, setStage] = useState<Stage>('starting');
  const [errorMsg, setErrorMsg] = useState('');
  const [pending, setPending] = useState<{ dataUrl: string; fileName: string } | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopStream = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
      video.load();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setStage('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setStage('camera');
    } catch {
      setErrorMsg('Camera access denied. Please allow camera access and reload.');
      setStage('error');
    }
  }, []);

  // Attach stream to video element when entering camera stage
  useEffect(() => {
    if (stage === 'camera' && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [stage]);

  useEffect(() => {
    startCamera();
    return stopStream;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    stopStream();
    setPending({ dataUrl, fileName: `photo_${Date.now()}.jpg` });
    setStage('review');
  }, [stopStream]);

  const handleRetake = useCallback(async () => {
    setPending(null);
    await startCamera();
  }, [startCamera]);

  const handleUpload = useCallback(async () => {
    if (!pending || !token) return;
    setStage('uploading');
    try {
      const res = await fetch(`/api/photo-relay/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageData: pending.dataUrl, fileName: pending.fileName }),
      });
      if (res.ok) {
        setStage('done');
      } else if (res.status === 404 || res.status === 410) {
        setErrorMsg('This link has expired or has already been used. Please scan a new QR code.');
        setStage('error');
      } else {
        setErrorMsg('Upload failed. Please try again.');
        setStage('error');
      }
    } catch {
      setErrorMsg('Upload failed. Please check your connection and try again.');
      setStage('error');
    }
  }, [pending, token]);

  return (
    <div className={styles.page}>
      {(stage === 'starting') && (
        <>
          <Spinner size="large" />
          <Text className={styles.statusText}>Starting camera...</Text>
        </>
      )}

      {stage === 'camera' && (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} autoPlay playsInline muted className={styles.video} />
          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={<CameraRegular />}
              size="large"
              onClick={handleCapture}
            >
              Capture
            </Button>
          </div>
        </>
      )}

      {stage === 'review' && pending && (
        <>
          <img src={pending.dataUrl} alt="Preview" className={styles.preview} />
          <div className={styles.actions}>
            <Button appearance="primary" size="large" onClick={handleUpload}>
              Use Photo
            </Button>
            <Button
              appearance="secondary"
              icon={<ArrowCounterclockwiseRegular />}
              size="large"
              onClick={handleRetake}
            >
              Retake
            </Button>
          </div>
        </>
      )}

      {stage === 'uploading' && (
        <>
          <Spinner size="large" />
          <Text className={styles.statusText}>Sending photo...</Text>
        </>
      )}

      {stage === 'done' && (
        <>
          <CheckmarkCircleRegular className={`${styles.statusIcon} ${styles.successIcon}`} />
          <Text size={600} weight="semibold" className={styles.statusText}>
            Photo captured!
          </Text>
          <Text className={styles.statusText}>You can close this tab and return to your form.</Text>
        </>
      )}

      {stage === 'error' && (
        <>
          <DismissCircleRegular className={`${styles.statusIcon} ${styles.errorIcon}`} />
          <Text size={500} weight="semibold" className={styles.statusText}>
            Something went wrong
          </Text>
          <Text className={styles.statusText}>{errorMsg}</Text>
          {errorMsg.includes('expired') ? null : (
            <Button appearance="primary" onClick={handleRetake}>
              Try Again
            </Button>
          )}
        </>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CaptureApp;
